import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, Zap, Heart, Monitor, Coffee, Globe, ArrowRight, X, Mail, CheckCircle, ChevronDown, Filter } from 'lucide-react';

const jobs = [
  {
    id: 1,
    title: 'Senior Quantitative Analyst',
    department: 'Quantitative Research',
    location: 'New York / Remote',
    type: 'Full-time',
    description: 'Develop and optimize high-frequency algorithmic strategies across Digital Assets and Forex. You will lead backtesting, risk modeling, and alpha discovery using our proprietary quant stack.',
    requirements: [
      'PhD or Masters in Math, Physics, or Quantitative Finance.',
      '5+ years experience in systematic trading or quant research.',
      'Expertise in Python, C++, and statistical modeling.',
      'Deep understanding of digital asset market micro-structure.'
    ]
  },
  {
    id: 2,
    title: 'Lead Full-Stack Blockchain Engineer',
    department: 'Engineering',
    location: 'Singapore / Remote',
    type: 'Full-time',
    description: 'Build and scale our institutional-grade digital asset infrastructure. You will be responsible for multi-sig wallet integrations, low-latency execution bridges, and secure settlement protocols.',
    requirements: [
      'BSc in Computer Science or related field.',
      'Proficiency in React, Node.js, and Rust/Solidity.',
      'Expertise in cryptographic security and SOC 2 frameworks.',
      'Experience with high-concurrency financial systems.'
    ]
  },
  {
    id: 3,
    title: 'Institutional Sales Director',
    department: 'Advisory & Growth',
    location: 'London / Dubai',
    type: 'Full-time',
    description: 'Manage and expand relationships with family offices, sovereign wealth funds, and UHNW allocators. You will be the face of Quantis Capital in the EMEA region.',
    requirements: [
      '10+ years in institutional sales or private banking.',
      'Strong network of existing institutional relationships.',
      'Deep knowledge of multi-asset portfolio construction.',
      'Exceptional communication and presentation skills.'
    ]
  },
  {
    id: 4,
    title: 'Risk & Compliance Officer',
    department: 'Operations',
    location: 'London / Remote',
    type: 'Full-time',
    description: 'Ensure our global operations maintain the highest standards of regulatory excellence. You will oversee KYC/AML frameworks and coordinate with SEC, FCA, and MAS regulators.',
    requirements: [
      '8+ years in financial compliance or regulatory oversight.',
      'Expertise in international AML/CFT standards.',
      'Experience with SOC 2 Type II and GIPS reporting.',
      'Strong legal background in financial services.'
    ]
  },
  {
    id: 5,
    title: 'Product Designer (Fintech)',
    department: 'Design',
    location: 'Remote',
    type: 'Contract / Full-time',
    description: 'Craft the future of institutional wealth management interfaces. You will design high-fidelity dashboards, interactive charts, and seamless investor onboarding flows.',
    requirements: [
      'Strong portfolio showcasing complex data visualization.',
      'Proficiency in Figma and interactive prototyping.',
      'Understanding of financial psychology and UX principles.',
      'Experience with institutional or "god-tier" aesthetics.'
    ]
  },
  {
    id: 6,
    title: 'DevOps & Infrastructure Architect',
    department: 'Engineering',
    location: 'Remote',
    type: 'Full-time',
    description: 'Architect a geo-distributed, zero-downtime infrastructure for our trading engines. Focus on security, scalability, and automated CI/CD pipelines.',
    requirements: [
      'Expertise in AWS, Kubernetes, and Terraform.',
      'Strong background in network security and firewalls.',
      'Experience with high-frequency trading infrastructure.',
      'Commitment to 99.99% uptime standards.'
    ]
  }
];

const Careers = () => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [activeFilter, setActiveFilter] = useState('All');

  const categories = ['All', 'Engineering', 'Quantitative Research', 'Advisory & Growth', 'Operations', 'Design'];

  const filteredJobs = activeFilter === 'All' 
    ? jobs 
    : jobs.filter(j => j.department === activeFilter);

  const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-80px' },
    transition: { duration: 0.6, delay }
  });

  return (
    <div style={{ paddingTop: '5rem', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', minHeight: '100vh' }}>
      
      {/* Hero Section */}
      <section style={{ padding: '6rem 5%', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)', width: '60vw', height: '60vw', background: 'radial-gradient(circle, var(--accent-primary) 0%, transparent 70%)', opacity: 0.05, filter: 'blur(100px)', zIndex: 0 }} />
        
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '900px', margin: '0 auto' }}>
          <motion.p {...fadeUp()} style={{ color: 'var(--accent-primary)', fontWeight: '700', fontSize: '0.9rem', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
            Careers at Quantis Capital
          </motion.p>
          <motion.h1 {...fadeUp(0.1)} style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)', fontWeight: '800', lineHeight: '1.1', marginBottom: '1.5rem', letterSpacing: '-0.03em' }}>
            Engineer the Future of <span className="gradient-text">Institutional Wealth</span>.
          </motion.h1>
          <motion.p {...fadeUp(0.2)} style={{ color: 'var(--text-secondary)', fontSize: '1.25rem', lineHeight: '1.7', maxWidth: '700px', margin: '0 auto' }}>
            We are looking for exceptional minds to build the world's most advanced multi-asset wealth platform. Join a global team of quants, engineers, and strategists.
          </motion.p>
        </div>
      </section>

      {/* Values / Culture */}
      <section style={{ padding: '4rem 5% 6rem', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '3rem' }}>
            {[
              { icon: <Zap size={28} />, title: 'High-Impact Work', desc: 'Every line of code and every research model directly influences the growth of $2.4B+ in managed assets.' },
              { icon: <Heart size={28} />, title: 'People-First Culture', desc: 'Remote-first flexibility, comprehensive health coverage, and a culture built on mutual respect and radical transparency.' },
              { icon: <Monitor size={28} />, title: 'Next-Gen Tech', desc: 'Work with a state-of-the-art stack across Rust, Python, and React, deployed on geo-distributed cloud infrastructure.' }
            ].map((v, i) => (
              <motion.div key={i} {...fadeUp(i * 0.1)}>
                <div style={{ background: 'rgba(252,213,53,0.1)', width: '56px', height: '56px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-primary)', marginBottom: '1.5rem' }}>{v.icon}</div>
                <h3 style={{ fontSize: '1.4rem', fontWeight: '800', marginBottom: '1rem' }}>{v.title}</h3>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7', fontSize: '1.05rem' }}>{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section style={{ padding: '6rem 5%' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '3rem', flexWrap: 'wrap', gap: '2rem' }}>
            <div style={{ maxWidth: '600px' }}>
              <h2 style={{ fontSize: '2.5rem', fontWeight: '800', marginBottom: '1rem' }}>Open Positions</h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem' }}>Explore our current openings and find your place in the future of finance.</p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveFilter(cat)}
                  style={{
                    padding: '0.6rem 1.2rem', borderRadius: '30px', fontWeight: '600', cursor: 'pointer',
                    background: activeFilter === cat ? 'var(--accent-primary)' : 'var(--bg-secondary)',
                    color: activeFilter === cat ? '#000' : 'var(--text-secondary)',
                    border: '1px solid var(--border-color)', transition: 'all 0.2s'
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem' }}>
            {filteredJobs.length > 0 ? (
              filteredJobs.map((job, i) => (
                <motion.div
                  key={job.id}
                  {...fadeUp(i * 0.05)}
                  onClick={() => setSelectedJob(job)}
                  whileHover={{ y: -5, borderColor: 'var(--accent-primary)' }}
                  style={{
                    background: 'var(--bg-secondary)', padding: '2rem', borderRadius: '24px',
                    border: '1px solid var(--border-color)', cursor: 'pointer', transition: 'all 0.2s',
                    display: 'flex', flexDirection: 'column'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <span style={{ background: 'rgba(252,213,53,0.1)', color: 'var(--accent-primary)', fontSize: '0.75rem', fontWeight: '700', padding: '0.3rem 0.8rem', borderRadius: '20px', textTransform: 'uppercase' }}>{job.department}</span>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', fontWeight: '600' }}>{job.type}</span>
                  </div>
                  <h3 style={{ fontSize: '1.4rem', fontWeight: '800', marginBottom: '0.8rem' }}>{job.title}</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                    <Globe size={14} /> {job.location}
                  </div>
                  <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6', fontSize: '0.95rem', marginBottom: '2rem', flexGrow: 1 }}>{job.description}</p>
                  <span style={{ color: 'var(--accent-primary)', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '1rem' }}>
                    View Details <ArrowRight size={16} />
                  </span>
                </motion.div>
              ))
            ) : (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '4rem', background: 'var(--bg-secondary)', borderRadius: '24px', border: '1px dashed var(--border-color)' }}>
                <Briefcase size={48} style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', opacity: 0.3 }} />
                <h3 style={{ color: 'var(--text-secondary)' }}>No positions currently open in this category.</h3>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section style={{ padding: '6rem 5%', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <motion.h2 {...fadeUp()} style={{ fontSize: '2.5rem', fontWeight: '800', textAlign: 'center', marginBottom: '1rem' }}>The Perks</motion.h2>
          <motion.p {...fadeUp(0.05)} style={{ textAlign: 'center', color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto 4rem', fontSize: '1.1rem' }}>We believe that exceptional talent should be exceptionally rewarded.</motion.p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem' }}>
            {[
              { icon: <Coffee size={24} />, title: 'Premium Workspaces', desc: 'Stunning HQs in NYC, London, and Singapore plus a home-office stipend.' },
              { icon: <Briefcase size={24} />, title: 'Performance Bonuses', desc: 'Industry-leading quarterly bonuses based on platform and portfolio performance.' },
              { icon: <Heart size={24} />, title: 'Unlimited PTO', desc: 'We trust our team to manage their time and take the rest they need.' },
              { icon: <Monitor size={24} />, title: 'Annual R&D Stipend', desc: '$5,000 annual allowance for books, courses, and global conferences.' }
            ].map((b, i) => (
              <motion.div key={i} {...fadeUp(i * 0.08)} style={{ background: 'var(--bg-primary)', padding: '2rem', borderRadius: '20px', border: '1px solid var(--border-color)' }}>
                <div style={{ color: 'var(--accent-primary)', marginBottom: '1rem' }}>{b.icon}</div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '800', marginBottom: '0.5rem' }}>{b.title}</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: '1.6' }}>{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '8rem 5%', textAlign: 'center' }}>
        <motion.div {...fadeUp()} style={{ maxWidth: '800px', margin: '0 auto' }}>
          <Mail size={48} style={{ color: 'var(--accent-primary)', marginBottom: '1.5rem' }} />
          <h2 style={{ fontSize: '2.5rem', fontWeight: '800', marginBottom: '1.5rem' }}>Don't see a perfect fit?</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '1.15rem', marginBottom: '2.5rem' }}>
            We're always looking for brilliant individuals to join our talent pool. Send your CV and a brief note on how you can contribute to <strong>careers@quantiscap.com</strong>.
          </p>
          <a 
            href="mailto:careers@quantiscap.com"
            style={{ 
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: 'var(--accent-gradient)', 
              padding: '1rem 2.5rem', borderRadius: '12px', color: '#000', fontWeight: '800', 
              textDecoration: 'none', transition: 'all 0.2s' 
            }}
          >
            Contact Talent Acquisition <ArrowRight size={18} />
          </a>
        </motion.div>
      </section>

      {/* Modal / Job Details Overlay */}
      <AnimatePresence>
        {selectedJob && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)',
              zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
            }}
            onClick={() => setSelectedJob(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={e => e.stopPropagation()}
              style={{
                width: '100%', maxWidth: '700px', background: 'var(--bg-modal)',
                borderRadius: '32px', border: '1px solid var(--border-color)',
                maxHeight: '90vh', overflowY: 'auto', position: 'relative'
              }}
            >
              <button 
                onClick={() => setSelectedJob(null)}
                style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '50%', padding: '0.5rem', color: 'var(--text-secondary)', cursor: 'pointer', zIndex: 10 }}
              ><X size={20} /></button>

              <div style={{ padding: '3rem 2.5rem' }}>
                <span style={{ color: 'var(--accent-primary)', fontWeight: '800', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{selectedJob.department}</span>
                <h2 style={{ fontSize: '2.2rem', fontWeight: '800', marginTop: '0.5rem', marginBottom: '1.5rem' }}>{selectedJob.title}</h2>
                
                <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '2.5rem', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)' }}><Globe size={16} /> {selectedJob.location}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)' }}><Monitor size={16} /> {selectedJob.type}</div>
                </div>

                <div style={{ marginBottom: '2.5rem' }}>
                  <h4 style={{ fontSize: '1.1rem', fontWeight: '800', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><CheckCircle size={18} color="var(--accent-primary)" /> The Role</h4>
                  <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7', fontSize: '1.05rem' }}>{selectedJob.description}</p>
                </div>

                <div style={{ marginBottom: '2.5rem' }}>
                  <h4 style={{ fontSize: '1.1rem', fontWeight: '800', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><CheckCircle size={18} color="var(--accent-primary)" /> Requirements</h4>
                  <ul style={{ paddingLeft: '1.2rem', color: 'var(--text-secondary)' }}>
                    {selectedJob.requirements.map((req, i) => (
                      <li key={i} style={{ marginBottom: '0.8rem', lineHeight: '1.5' }}>{req}</li>
                    ))}
                  </ul>
                </div>

                <button 
                  onClick={() => window.location.href = `mailto:careers@quantiscap.com?subject=Application: ${selectedJob.title}`}
                  style={{ width: '100%', padding: '1.2rem', borderRadius: '14px', background: 'var(--accent-gradient)', color: '#000', fontWeight: '800', fontSize: '1.1rem', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                >
                  Apply via Email <Mail size={20} />
                </button>
                <p style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: '1rem' }}>Include your CV and a brief cover letter.</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default Careers;
